import { SxProps } from '@mui/system';
import { SlButtonVariant, hexToRgba, useStacklineTheme } from '@stackline/ui';
import React, { useMemo, forwardRef, ForwardedRef } from 'react';
import { DynamicDownChevron } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/DynamicDownChevron';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { SlMenu, SlMenuProps } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlMenu';

export interface SlDropdownMenuProps extends Omit<SlMenuProps, 'open'> {
  /**
   * Label to show when nothing has been selected yet
   */
  defaultLabel?: string | React.ReactNode;
  disabled?: boolean;
  /**
   * Variant for the button that is rendered for clicking
   * to display the options.
   */
  buttonVariant?: SlButtonVariant;

  /**
   * If set, we will show n number of options
   * with a "+ View More" option at the bottom
   */
  amountOfOptionsToShow?: number;

  /**
   * If true, shows only enabled (non-disabled) options by default.
   * Disabled options are revealed when the user clicks "+ View More".
   */
  hideDisabledUntilExpanded?: boolean;

  /**
   * If true, we will not set fixed menu width with 160px,
   * so the ellipsis for slMenu won't be applied
   */
  shouldShowFullItemText?: boolean;

  /**
   * Optional styles to apply to the dropdown menu options
   */
  menuStyles?: SxProps;

  /**
   * Optional styles to apply to the dropdown menu items text component
   */
  menuItemTextStyles?: SxProps;

  /**
   * Optional props to pass to the SlButton component used in the SlDropdownMenu.
   * This is useful for setting a fixed width on the main SlDropdownMenu Button via the buttonSx.
   * Example: stacklineButtonProps={{ buttonSx: { width: '152px' }}
   */
  stacklineButtonProps?: React.ComponentProps<typeof SlButton>;

  slMenuProps?: Omit<SlMenuProps, 'options' | 'selectedId' | 'onChange' | 'open'>;
  name?: string;
  customEndIcon?: React.ReactNode;
}

/**
 * Common dropdown menu to be used throughout the Stackline site.
 */
export const SlDropdownMenu = forwardRef(function SlDropdownMenu(
  {
    options,
    defaultLabel = '',
    selectedId,
    onChange,
    disabled,
    buttonVariant = 'normal',
    shouldShowFullItemText = false,
    amountOfOptionsToShow,
    hideDisabledUntilExpanded,
    anchorOrigin,
    transformOrigin,
    customEndIcon,
    iconStyle,
    slMenuProps,
    menuStyles = {},
    menuItemTextStyles = {},
    stacklineButtonProps = {}
  }: SlDropdownMenuProps & { iconStyle?: React.CSSProperties },
  ref: ForwardedRef<HTMLDivElement>
) {
  const theme = useStacklineTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const label = useMemo(() => {
    const selectedOption = options.find((option) => option.id === selectedId);
    return selectedOption ? selectedOption.label : defaultLabel;
  }, [selectedId, options, defaultLabel]);

  const normalizedPaperMenuStyles: Record<string, unknown> =
    typeof menuStyles === 'function' || Array.isArray(menuStyles) ? {} : (menuStyles as Record<string, unknown>);

  return (
    <div ref={ref}>
      <SlButton
        onClick={handleClick}
        disabled={disabled}
        endIcon={(() => {
          if (customEndIcon !== undefined) {
            return customEndIcon;
          }

          if (buttonVariant === 'title') {
            return (
              <DynamicDownChevron
                strokeWidth="2px"
                color={disabled ? hexToRgba(theme.colors.secondary, 0.5) : theme.colors.primary}
                style={{ marginTop: '-2px', verticalAlign: 'bottom', ...iconStyle }}
              />
            );
          }

          return (
            <DynamicDownChevron
              color={disabled ? hexToRgba(theme.colors.secondary, 0.5) : theme.colors.primary}
              style={{ verticalAlign: 'bottom', ...iconStyle }}
            />
          );
        })()}
        variant={buttonVariant}
        buttonSx={
          buttonVariant === 'title'
            ? {
                padding: 0
              }
            : undefined
        }
        {...stacklineButtonProps}
      >
        {label}
      </SlButton>
      <SlMenu
        menuItemTextStyles={menuItemTextStyles}
        amountOfOptionsToShow={amountOfOptionsToShow}
        hideDisabledUntilExpanded={hideDisabledUntilExpanded}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        anchorOrigin={
          anchorOrigin || {
            vertical: 'bottom',
            horizontal: 'left'
          }
        }
        transformOrigin={
          transformOrigin || {
            vertical: 'top',
            horizontal: 'left'
          }
        }
        options={options}
        selectedId={selectedId}
        onChange={onChange}
        sx={{
          '& ul': {
            paddingY: 0
          },
          '.MuiPaper-root': {
            maxHeight: 'calc(100% - 59px)',
            ...(shouldShowFullItemText ? {} : { width: 'auto' }),
            paddingRight: 0,
            boxSizing: 'border-box',
            '&::-webkit-scrollbar': { display: 'none' },
            ...normalizedPaperMenuStyles
          },
          '&::-webkit-scrollbar': { display: 'none' }
        }}
        {...slMenuProps}
      />
    </div>
  );
});
